import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { Box, Typography, useTheme } from '@mui/material';

interface RecaptchaComponentProps {
  sitekey: string;
  onChange?: (token: string | null) => void;
  onExpired?: () => void;
  onError?: () => void;
  size?: 'compact' | 'normal';
  theme?: 'light' | 'dark';
  disabled?: boolean;
  errorMessage?: string;
  'data-testid'?: string;
}

export interface RecaptchaRef {
  reset: () => void;
  execute: () => Promise<string | null>;
  getValue: () => string | null;
}

const RecaptchaComponent = forwardRef<RecaptchaRef, RecaptchaComponentProps>(
  (
    {
      sitekey,
      onChange,
      onExpired,
      onError,
      size = 'normal',
      theme,
      disabled = false,
      errorMessage,
      'data-testid': testId,
    },
    ref
  ) => {
    const recaptchaRef = useRef<ReCAPTCHA>(null);
    const muiTheme = useTheme();

    // Detecta tema automaticamente se não especificado
    const recaptchaTheme = theme || (muiTheme.palette.mode === 'dark' ? 'dark' : 'light');

    useImperativeHandle(ref, () => ({
      reset: () => {
        recaptchaRef.current?.reset();
      },
      execute: async () => {
        if (recaptchaRef.current) {
          return await recaptchaRef.current.executeAsync();
        }
        return null;
      },
      getValue: () => {
        return recaptchaRef.current?.getValue() || null;
      },
    }));

    const handleChange = (token: string | null) => {
      if (onChange) {
        onChange(token);
      }
    };

    const handleExpired = () => {
      if (onExpired) {
        onExpired();
      }
    };

    const handleError = () => {
      console.error('reCAPTCHA error occurred');
      if (onError) {
        onError();
      }
    };

    if (!sitekey) {
      console.warn('RecaptchaComponent: sitekey is required');
      return (
        <Box data-testid={testId}>
          <Typography variant="caption" color="error">
            reCAPTCHA configuration error: sitekey not found
          </Typography>
        </Box>
      );
    }

    return (
      <Box
        data-testid={testId}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 0.5,
          opacity: disabled ? 0.6 : 1,
          pointerEvents: disabled ? 'none' : 'auto',
        }}
      >
        <ReCAPTCHA
          ref={recaptchaRef}
          sitekey={sitekey}
          onChange={handleChange}
          onExpired={handleExpired}
          onErrored={handleError}
          size={size}
          theme={recaptchaTheme}
          hl="pt-BR" // Idioma português brasileiro
        />
        
        {errorMessage && (
          <Typography 
            variant="caption" 
            color="error" 
            sx={{ mt: 0.5 }}
            role="alert"
            aria-live="polite"
          >
            {errorMessage}
          </Typography>
        )}
      </Box>
    );
  }
);

RecaptchaComponent.displayName = 'RecaptchaComponent';

export default RecaptchaComponent;